
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X } from "lucide-react";
import { BlogPostFormData } from "@/types/blog";

interface SEOTabProps {
  formData: BlogPostFormData;
  onChange: (field: string, value: any) => void;
  isLoading?: boolean;
}

export const SEOTab: React.FC<SEOTabProps> = ({
  formData,
  onChange,
  isLoading = false
}) => {
  const [newTag, setNewTag] = useState("");

  // Handle slug changes with automatic formatting (lowercase and hyphenation)
  const handleSlugChange = (value: string) => {
    // Convert to lowercase and replace spaces with hyphens
    const formattedSlug = value.toLowerCase().replace(/\s+/g, '-');
    onChange("slug", formattedSlug);
  };

  // Handle tag addition with lowercase formatting
  const handleTagAdd = () => {
    if (newTag.trim() && formData.metadata) {
      const lowercaseTag = newTag.trim().toLowerCase();
      const updatedTags = [...(formData.metadata.tags || []), lowercaseTag];
      onChange("metadata", { ...formData.metadata, tags: updatedTags });
      setNewTag("");
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    if (formData.metadata) {
      const updatedTags = (formData.metadata.tags || []).filter((tag) => tag !== tagToRemove);
      onChange("metadata", { ...formData.metadata, tags: updatedTags });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          placeholder="URL slug (e.g., my-blog-post)"
          value={formData.slug}
          onChange={(e) => handleSlugChange(e.target.value)}
          disabled={isLoading}
        />
      </div>
      
      <div className="space-y-2">
        <Label>Tags</Label>
        <div className="flex space-x-2">
          <Input
            type="text"
            placeholder="New tag"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleTagAdd();
              }
            }}
            disabled={isLoading}
          />
          <Button type="button" onClick={handleTagAdd} disabled={isLoading}>
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(formData.metadata?.tags || []).map((tag) => (
            <Button
              key={tag}
              variant="outline"
              size="sm"
              onClick={() => handleTagRemove(tag)}
              disabled={isLoading}
            >
              {tag}
              <X className="ml-2 h-4 w-4" />
            </Button>
          ))}
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="seo_description">SEO Description</Label>
        <Textarea
          id="seo_description"
          placeholder="SEO description (appears in search results)"
          rows={3}
          value={formData.metadata?.seo_description || ""}
          onChange={(e) => {
            const newMetadata = { ...formData.metadata, seo_description: e.target.value };
            onChange("metadata", newMetadata);
          }}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};
